import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Axios from "axios";

// UI Components
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';

// Icons
import { 
  EllipsisVerticalIcon, 
  TrashIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  MagnifyingGlassIcon,
  DocumentDuplicateIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';

const TemplateTable = ({ templates = [], storageUsed, preferredLanguage }) => {
  const backEndUrl = process.env.REACT_APP_SERVER_ADDRESS;
  const navigate = useNavigate();

  // --- STATE ---
  const [templateToDelete, setTemplateToDelete] = useState(null);
  
  // Dialog State
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  
  // Menu State
  const [openMenuRowId, setOpenMenuRowId] = useState(null);
  const menuRef = useRef(null);

  // Filter State
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 9;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuRowId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- ACTIONS ---

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setOpenDeleteDialog(true);
    setOpenMenuRowId(null);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      await Axios({
        method: 'DELETE',
        withCredentials: true,
        data: {
            template_id: templateToDelete._id,
            documents: templateToDelete.documents || [],
            storageUsed : storageUsed, // Passed from prop
        },
        url: `${backEndUrl}/updateClient/deleteTemplate`,
      });
      setOpenDeleteDialog(false);
      window.location.reload();
    } catch (error) {
      console.error("Error deleting template:", error);
      alert("Error deleting template.");
    }
  };

  // --- HELPERS ---

  const getLocalizedValue = (rawField, userLang) => {
    if (!rawField) return '';
    if (typeof rawField === 'string') return rawField;
    if (rawField && typeof rawField === 'object') {
        if (userLang === 'french') {
            return rawField.fr || rawField.en || ''; 
        } else {
            return rawField.en || '';
        }
    }
    return ''; 
  };

  // --- RENDER PREPARATION ---

  // Filter
  let processedTemplates = templates || [];
  
  if (searchQuery) {
      processedTemplates = processedTemplates.filter(t => {
          const name = getLocalizedValue(t.name, preferredLanguage).toLowerCase();
          return name.includes(searchQuery.toLowerCase());
      });
  }

  // Pagination
  const totalPages = Math.ceil(processedTemplates.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const displayTemplates = processedTemplates.slice(indexOfFirstRow, indexOfLastRow);

  return (
    <div className="bg-white rounded-2xl shadow-lg border-2 w-full overflow-hidden mt-6 font-sans backdrop-blur-sm">
      
      {/* HEADER */}
      <div className="px-8 py-6 border-b border-slate-200/60 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gradient-to-r from-slate-50/50 to-white">
        <div>
            <h3 className="font-bold text-2xl text-slate-900 tracking-tight">Templates</h3>
            <p className="text-sm text-slate-500 mt-1">{processedTemplates.length} {processedTemplates.length === 1 ? 'template' : 'templates'}</p>
        </div>
        <div className="flex items-center gap-4">
            <div className="relative">
                <input 
                  type="text" 
                  placeholder="Search templates..." 
                  className="pl-10 pr-4 py-2.5 border-slate-300 rounded-xl text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none w-72 text-slate-700 bg-white shadow-sm transition-all"
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <MagnifyingGlassIcon className="h-4 w-4 text-slate-400 absolute left-3.5 top-3.5"/>
            </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto min-h-[300px] bg-gradient-to-b from-white to-slate-50/30">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="text-slate-500 text-xs font-semibold uppercase tracking-wider border-b border-slate-200/60 bg-slate-50/80 backdrop-blur-sm">
              <th className="px-8 py-5 pl-8">Template Name</th>
              <th className="px-6 py-5">Documents</th>
              <th className="px-6 py-5 text-right pr-8">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {displayTemplates.map((template) => {
              const name = getLocalizedValue(template.name, preferredLanguage);
              const docCount = template.documents ? template.documents.length : 0;
              
              // Helper to generate consistent color based on name string
              const getAvatarColor = (name) => {
                const colors = ['bg-red-100 text-red-600', 'bg-orange-100 text-orange-600', 'bg-amber-100 text-amber-600', 'bg-emerald-100 text-emerald-600', 'bg-teal-100 text-teal-600', 'bg-cyan-100 text-cyan-600', 'bg-blue-100 text-blue-600', 'bg-indigo-100 text-indigo-600', 'bg-violet-100 text-violet-600', 'bg-fuchsia-100 text-fuchsia-600', 'bg-pink-100 text-pink-600', 'bg-rose-100 text-rose-600'];
                let hash = 0;
                for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
                return colors[Math.abs(hash) % colors.length];
              };

              const avatarClass = getAvatarColor(name);

              return (
                <tr 
                  key={template._id} 
                  className={`group transition-all duration-300 hover:bg-blue-50 hover:shadow-sm`}
                >
                  <td className="px-8 py-5 pl-8 border-l-4 border-l-transparent group-hover:border-l-blue-600 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm shadow-sm ${avatarClass}`}>
                        <DocumentDuplicateIcon className="h-5 w-5" />
                      </div>
                      <div className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors text-base">
                        {name}
                      </div>
                    </div>
                  </td>

                  <td className="px-6 py-5">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                      {docCount} Documents
                    </span>
                  </td>

                  <td className="px-6 py-5 pr-8 text-right relative">
                      <div className="relative inline-block text-left">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuRowId(openMenuRowId === template._id ? null : template._id);
                          }}
                          className="text-slate-600 hover:text-blue-600 p-2.5 rounded-lg hover:bg-blue-50 hover:shadow-md transition-all group-hover:bg-blue-50 group-hover:text-blue-600 border-transparent hover:border-blue-200"
                        >
                          <EllipsisVerticalIcon className="h-6 w-6" />
                        </button>

                        {openMenuRowId === template._id && (
                          <div ref={menuRef} className="absolute right-0 top-10 w-48 bg-white border-slate-200 rounded-xl shadow-2xl z-50 overflow-hidden text-left animate-in fade-in zoom-in-95 duration-200 ring-1 ring-slate-900/5">
                            <Link 
                                to={`/dashboard/templates/${template._id}`}
                                className="w-full text-left block px-4 py-3 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-3 transition-colors no-underline"
                            >
                              <PencilSquareIcon className="h-4 w-4" /> Modify
                            </Link>
                            <div className="h-px bg-slate-100 my-1"></div>
                            <button 
                              onClick={() => handleDeleteClick(template)}
                              className="w-full text-left px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                  </td>
                </tr>
              );
            })}
            
            {displayTemplates.length === 0 && (
                <tr>
                    <td colSpan="3" className="px-8 py-20 text-center">
                        <div className="flex flex-col items-center justify-center">
                            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                                <MagnifyingGlassIcon className="h-8 w-8 text-slate-400" />
                            </div>
                            <p className="text-slate-500 font-medium text-base">
                                No templates found.
                            </p>
                        </div>
                    </td>
                </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="px-8 py-5 border-t border-slate-200/60 flex justify-between items-center bg-gradient-to-r from-slate-50/50 to-white">
            <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="flex items-center gap-2 text-sm font-semibold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:text-blue-600 transition-all px-4 py-2 rounded-lg hover:bg-blue-50 disabled:hover:bg-transparent"
            >
                <ChevronLeftIcon className="h-4 w-4" /> Previous
            </button>
            <div className="flex items-center gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded-lg text-sm font-semibold transition-all ${
                            currentPage === page
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'text-slate-600 hover:bg-slate-100'
                        }`}
                    >
                        {page}
                    </button>
                ))}
            </div>
            <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="flex items-center gap-2 text-sm font-semibold text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:text-blue-600 transition-all px-4 py-2 rounded-lg hover:bg-blue-50 disabled:hover:bg-transparent"
            >
                Next <ChevronRightIcon className="h-4 w-4" />
            </button>
        </div>
      )}

      <React.Fragment>
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{"Delete this template?"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete this template? This will also delete all associated documents. <br/>
              <span className="text-red-500 font-bold text-sm mt-2 block">⚠️ This action is irreversible.</span>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)} color="primary">Cancel</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>

    </div>
  );
};

export default TemplateTable;